<template>
  <div
    class="card"
    :class="[classes, linkUrl ? 'has-link' : '']"
  >
    <div
      v-if="src"
      class="card-top"
    >
      <img
        class="card-img"
        :alt="alt"
        :src="src"
      >
    </div>
    <div class="card-body">
      <slot name="card-body" />
    </div>
    <a
      v-if="linkUrl"
      :href="linkUrl"
      :target="hasExternalLink(linkUrl) ? '_blank' : null"
      :rel="hasExternalLink(linkUrl) ? 'noopener noreferrer' : null"
      class="card-link"
      :aria-label="description"
    />
  </div>
</template>

<script>
export default {
  name: 'KfCard',
  props: {
    src: {
      type: String,
      default: null,
    },
    alt: {
      type: String,
      default: null,
    },
    description: {
      type: String,
      default: null,
    },
    linkUrl: {
      type: String,
      default: null,
    },
    centered: {
      type: Boolean,
      default: false,
    },
    review: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    classes() {
      return {
        'is-centered': this.centered,
        'is-review': this.review,
      };
    },
  },
  methods: {
    hasExternalLink(url) {
      const pattern = /^((http|https):\/\/)/;
      let isExernal = false;

      if (pattern.test(url)) {
        isExernal = true;
      }

      return isExernal;
    },
  },
};
</script>

<style lang="postcss">
.card {
  @apply flex flex-col relative rounded-sm overflow-hidden mb-7;
  box-shadow: 0px 4px 20px rgb(54, 25, 5, 0.12);

  &:last-of-type {
    @apply mb-0;
  }

  @screen md {
    @apply mb-0;
  }

  &.has-link {
    &:hover {
      box-shadow: none;

      .card-top {
        &::before {
          @apply block;
          background-image: url('~@/assets/images/curve-news-gray.svg?inline');
        }
      }

      .card-body,
      .card-bottom {
        @apply bg-primary-delta;
      }
    }
  }

  &-top {
    @apply flex flex-col relative rounded-t-sm overflow-hidden;

    &::before {
      @apply block absolute;
      content: '';
      background-position: center bottom;
      background-repeat: no-repeat;
      padding-bottom: 9.7222222222vw;
      background-size: 100%;
      bottom: -1px;
      left: -1px;
      background-image: url('~@/assets/images/curve-news-white.svg?inline');
      width: calc(100% + 2px);
    }
  }

  .richtext {
    @apply mb-4;
  }

  &-body {
    @apply flex flex-col bg-white px-5 pt-6 pb-10 overflow-hidden flex-auto justify-start;

    @screen md {
      @apply px-7 py-10;
    }

    > *:last-child {
      @apply mb-0;
    }

    .richtext-download-links,
    .richtext-external-links,
    .richtext-arrow-links {
      > a {
        @apply my-3;
      }
    }

    .richtext {
      p,
      ul,
      li,
      a {
        @apply text-base;

        @screen lg {
          @apply text-lg;
        }
      }
    }
  }

  &.is-centered {
    .card-body {
      @apply flex items-center text-center;
    }
  }

  &-img {
    @apply object-cover object-center;
    height: 240px;
  }

  &-title {
    @apply text-primary-bravo;
  }

  &-subtitle {
    @apply text-gray-300 text-base leading-normal mb-4;
  }

  &-description {
    @apply italic mb-4;
  }

  &-link {
    @apply no-underline text-primary-bravo;

    &::after {
      @apply absolute top-0 right-0 bottom-0 left-0;
      content: '';
    }

    &-span {
      @apply relative pl-7 inline-block w-full font-medium pointer-events-none mt-auto;

      &:before {
        @apply absolute bg-no-repeat left-0 w-8 h-8;
        content: '';
        top: 8px;
        background-size: 12px 12px;
        background-image: url('~@/assets/svg/chevron-right-secondary.svg?inline');
      }

      .theme-secondary & {
        &:before {
          background-image: url('~@/assets/svg/theme-secondary/ts-chevron-right-secondary.svg?inline');
        }
      }
    }
  }

  &.is-review {
    .card-top {
      &::after {
        content: '';
        @apply absolute bg-no-repeat bg-center top-0 left-0 right-0 bottom-0;
        background-size: 64px 64px;
        background-image: url('~@/assets/svg/video-play-button.svg?inline');
      }

      .theme-secondary & {
        &::after {
          background-image: url('~@/assets/svg/theme-secondary/ts-video-play-button.svg?inline');
        }
      }
    }

    &:hover {
      .card-top {
        &::after {
          background-image: url('~@/assets/svg/video-play-button-hover.svg?inline');
        }

        .theme-secondary & {
          &::after {
            background-image: url('~@/assets/svg/theme-secondary/ts-video-play-button-hover.svg?inline');
          }
        }
      }
    }

    .card-title {
      @apply text-3xl font-playfair;

      @screen md {
        @apply text-5xl;
      }
    }
  }
}
</style>
